import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import create from 'zustand';
import { LoginType } from 'features/login/types';
import alertMessage from 'global/AlertMessage';
import {
  requestVerifyIdentity,
  requestVerifyPassword,
  requestVerifyPin,
  requestOtpTrustedDevice,
  verifyOtpTrustedDevice,
  requestSendPrompt,
  getPromptResult,
  verifyPromptRequest,
  requestForgotPin,
} from 'lib/api/trustedDevice';
import { TLoginType, PROMPT_STATUS } from './types';
import { calculateRemainingOtpSecond, mapOtpOptions } from './utils';
import {
  trackerIdentityVerificationFailed,
  trackerInputPasswordSuccess,
  trackerInputPinSuccess,
  trackerLoginCompleted,
} from './tracker';
import { OTPChannelTypes } from 'features/securities/setting/types';
import { AuthenticateMultiFactorRes } from 'typings/mfa';
import { OTPOptions } from 'features/edit-profile/types';

interface State {
  loginType: TLoginType;
  newDeviceConfig: AuthenticateMultiFactorRes;
  trustedDevice: {
    token: string;
    deviceName: string;
    /// Prompt Section ///
    promptToken: string;
    promptRemainingSecond: number;
    promptTarget: string;
    promptStatus: PROMPT_STATUS;
    ackPrompToken: string;
    /// ----------- ///
    /// OTP Section ///
    otpRecipients: OTPOptions[];
    channel: OTPChannelTypes;
    otp: string;
    target: string;
    remainingSecond: number;
    /// ----------- ///
    /// API Call State ///
    isSubmitting: boolean;
    errorText: string;
    errors: any[];
  };
}

interface Actions {
  updateLoginType(type: LoginType): void;
  resetLoginType(): void;
  updatenewDeviceConfig(params: AuthenticateMultiFactorRes): void;
  resetNewDeviceConfig: () => void;
  updateTrustedDevice({
    token,
    promptToken,
    promptRemainingSecond,
    promptTarget,
    promptStatus,
    ackPrompToken,
    channel,
    deviceName,
    otpRecipients,
    otp,
    target,
    remainingSecond,
    isSubmitting,
    errorText,
    errors,
  }: {
    token?: string;
    promptToken?: string;
    promptRemainingSecond?: number;
    promptTarget?: string;
    promptStatus?: PROMPT_STATUS;
    ackPrompToken?: string;
    deviceName?: string;
    otpRecipients?: OTPOptions[];
    channel?: OTPChannelTypes;
    otp?: string;
    target?: string;
    remainingSecond?: number;
    isSubmitting?: boolean;
    errorText?: string;
    errors?: any[];
  }): void;
  resetTrustedDevice: () => void;
  requestValidateIdentity({
    ktp,
    dob,
    bankAccount,
    mothersName,
    cb,
  }: {
    ktp: string;
    dob: string;
    bankAccount: string;
    mothersName: string;
    cb: () => void;
  }): Promise<void>;
  requestValidatePassword({
    password,
    cb,
  }: {
    password: string;
    cb: () => void;
  }): Promise<void>;
  requestValidatePin({
    pin,
    cb,
  }: {
    pin: string;
    cb: () => void;
  }): Promise<void>;
  requestForgotPIN(): Promise<void>;
  requestOtpTrustedDevice(): Promise<void>;
  verifyOtpTrustedDevice(cb: (any) => void): Promise<void>;
  requestPromptTrustedDevice(): Promise<void>;
  getResultPrompTrustedDevice(): Promise<void>;
  verifyPromptTrustedDevice(cb: (any) => void): Promise<void>;
}

const initialState: State = {
  loginType: LoginType.LOGIN_DATA_TYPE_UNSPECIFIED,
  newDeviceConfig: {
    login_token: '',
    verification_token: '',
  },
  trustedDevice: {
    token: '',
    deviceName: '',
    /// Prompt Section ///
    promptToken: '',
    promptRemainingSecond: 0,
    promptTarget: '',
    promptStatus: PROMPT_STATUS.PROMPT_STATUS_UNSPECIFIED,
    ackPrompToken: '',
    /// ----------- ///
    /// OTP Section ///
    otpRecipients: [],
    channel: OTPChannelTypes.EMAIL,
    otp: '',
    target: '',
    remainingSecond: 0,
    /// ----------- ///
    /// API Call State ///
    isSubmitting: false,
    errorText: '',
    errors: [],
    /// ----------- ///
  },
};

export const useTrustedDeviceStore = create<State & Actions>()(
  persist(
    immer((set, get) => ({
      ...initialState,
      updateLoginType: (loginType) => set({ loginType }),
      resetLoginType: () => set({ loginType: initialState.loginType }),
      updatenewDeviceConfig: (params) =>
        set((draft) => {
          draft.newDeviceConfig = params;
        }),
      resetNewDeviceConfig: () => {
        get().updatenewDeviceConfig(initialState.newDeviceConfig);
      },
      updateTrustedDevice: ({
        token,
        promptToken,
        promptRemainingSecond,
        promptTarget,
        ackPrompToken,
        promptStatus,
        channel,
        deviceName,
        otpRecipients,
        otp,
        target,
        remainingSecond,
        isSubmitting,
        errorText,
        errors,
      }) =>
        set((draft) => {
          draft.trustedDevice.token = token ?? draft.trustedDevice.token;

          draft.trustedDevice.promptToken =
            promptToken ?? draft.trustedDevice.promptToken;

          draft.trustedDevice.promptRemainingSecond =
            promptRemainingSecond ?? draft.trustedDevice.promptRemainingSecond;

          draft.trustedDevice.promptTarget =
            promptTarget ?? draft.trustedDevice.promptTarget;

          draft.trustedDevice.ackPrompToken =
            ackPrompToken ?? draft.trustedDevice.ackPrompToken;

          draft.trustedDevice.promptStatus =
            promptStatus ?? draft.trustedDevice.promptStatus;

          draft.trustedDevice.deviceName =
            deviceName ?? draft.trustedDevice.deviceName;

          draft.trustedDevice.otpRecipients =
            otpRecipients ?? draft.trustedDevice.otpRecipients;

          draft.trustedDevice.channel = channel ?? draft.trustedDevice.channel;

          draft.trustedDevice.otp = otp ?? draft.trustedDevice.otp;

          draft.trustedDevice.target = target ?? draft.trustedDevice.target;

          draft.trustedDevice.remainingSecond =
            remainingSecond ?? draft.trustedDevice.remainingSecond;

          draft.trustedDevice.isSubmitting =
            isSubmitting ?? draft.trustedDevice.isSubmitting;

          draft.trustedDevice.errorText =
            errorText ?? draft.trustedDevice.errorText;

          draft.trustedDevice.errors = errors ?? draft.trustedDevice.errors;
        }),
      resetTrustedDevice: () => {
        get().updateTrustedDevice(initialState.trustedDevice);
      },
      requestValidateIdentity: async ({
        ktp,
        dob,
        bankAccount,
        mothersName,
        cb,
      }) => {
        try {
          get().updateTrustedDevice({
            isSubmitting: true,
            errorText: '',
            errors: [],
          });
          const { token } = get().trustedDevice;

          const response: any = await requestVerifyIdentity({
            token,
            identity: ktp,
            birthDate: dob,
            bankAccountNumber: bankAccount,
            motherName: mothersName,
          });
          get().updateTrustedDevice({ token: response?.data?.data?.token });
          cb();
        } catch (err) {
          trackerIdentityVerificationFailed();
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
            errors: err?.cause?.errors,
          });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestValidatePassword: async ({ password, cb }) => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token } = get().trustedDevice;

          await requestVerifyPassword({
            token,
            password,
          });
          trackerInputPasswordSuccess();
          cb();
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestValidatePin: async ({ pin, cb }) => {
        try {
          const { token } = get().trustedDevice;

          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const response = await requestVerifyPin({ pin, token });
          get().updateTrustedDevice({
            otpRecipients: mapOtpOptions(response?.data?.data?.otp_recipients),
          });
          trackerInputPinSuccess();
          cb();
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestForgotPIN: async () => {
        try {
          const { token } = get().trustedDevice;

          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const response = await requestForgotPin({ token });
          alertMessage({
            content: response?.data?.message,
            alertType: 'snackbar-dark',
            messageType: 'success',
            hasCloseIcon: false,
          });
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestOtpTrustedDevice: async () => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token, channel } = get().trustedDevice;

          const response = await requestOtpTrustedDevice({ token, channel });
          get().updateTrustedDevice({
            target: response?.data?.data?.target,
            remainingSecond: calculateRemainingOtpSecond(
              response?.data?.data?.next_attempt_time,
            ),
          });
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      verifyOtpTrustedDevice: async (cb) => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token, otp } = get().trustedDevice;

          const response = await verifyOtpTrustedDevice({
            token,
            otp,
          });
          get().updateLoginType(LoginType.LOGIN_DATA_TYPE_SUCCESS);
          trackerLoginCompleted();
          cb(response?.data?.data);
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestPromptTrustedDevice: async () => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token } = get().trustedDevice;

          const response = await requestSendPrompt({
            token,
          });
          get().updateTrustedDevice({
            promptToken: response?.data?.data?.prompt_token,
            promptTarget: response?.data?.data?.target,
            promptRemainingSecond: calculateRemainingOtpSecond(
              response?.data?.data?.next_attempt_time,
            ),
          });
        } catch (err) {
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
          });
          throw err;
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      getResultPrompTrustedDevice: async () => {
        try {
          const { promptToken } = get().trustedDevice;
          const response = await getPromptResult(promptToken);
          get().updateTrustedDevice({
            ackPrompToken: response?.data?.data?.acknowledge_token,
            promptStatus: response?.data?.data?.status,
          });
        } catch (err) {
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
          });
        }
      },
      verifyPromptTrustedDevice: async (cb) => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });

          const { token, ackPrompToken } = get().trustedDevice;

          const response = await verifyPromptRequest({
            token,
            acknowledgeToken: ackPrompToken,
          });
          get().updateLoginType(LoginType.LOGIN_DATA_TYPE_SUCCESS);
          trackerLoginCompleted();
          cb(response?.data?.data);
        } catch (err) {
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
          });
        }
      },
    })),
    {
      name: 'trustedDevice',
      // partialize: (state) => ({ loginType: state.loginType }),
    },
  ),
);
